import type { PathTileAtlasId } from '../pathArtStyles';
import { fillMaskedCanvasWithTexture } from './pathTextureFill';

export interface ComposePathTileOptions {
  readonly atlasId: PathTileAtlasId;
  readonly tileSize: number;
  readonly outerMask?: HTMLCanvasElement | null;
  readonly innerMask: HTMLCanvasElement;
  readonly textureImage?: HTMLImageElement;
  readonly textureTileScale?: number;
  readonly outerExpansionPx?: number;
  readonly decorateInner?: ((canvas: HTMLCanvasElement, mask: HTMLCanvasElement) => void) | null;
}

export function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export function hashSeed(seed: string): number {
  let hash = 2166136261;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function seededFloat(seed: string): number {
  return (hashSeed(seed) % 10000) / 10000;
}

export function expandCanvasAlpha(source: HTMLCanvasElement, radius: number): HTMLCanvasElement {
  if (radius <= 0) {
    return source;
  }
  const sourceContext = source.getContext('2d');
  if (!sourceContext) {
    return source;
  }
  const sourceImage = sourceContext.getImageData(0, 0, source.width, source.height);
  const output = createCanvas(source.width, source.height);
  const outputContext = output.getContext('2d');
  if (!outputContext) {
    return source;
  }
  const outputImage = outputContext.createImageData(source.width, source.height);
  const sourceData = sourceImage.data;
  const outputData = outputImage.data;

  for (let y = 0; y < source.height; y += 1) {
    for (let x = 0; x < source.width; x += 1) {
      const sourceIndex = ((y * source.width) + x) * 4;
      if (sourceData[sourceIndex + 3] === 0) {
        continue;
      }
      for (let offsetY = -radius; offsetY <= radius; offsetY += 1) {
        for (let offsetX = -radius; offsetX <= radius; offsetX += 1) {
          const targetX = x + offsetX;
          const targetY = y + offsetY;
          if (targetX < 0 || targetY < 0 || targetX >= source.width || targetY >= source.height) {
            continue;
          }
          const targetIndex = ((targetY * source.width) + targetX) * 4;
          outputData[targetIndex] = sourceData[sourceIndex];
          outputData[targetIndex + 1] = sourceData[sourceIndex + 1];
          outputData[targetIndex + 2] = sourceData[sourceIndex + 2];
          outputData[targetIndex + 3] = sourceData[sourceIndex + 3];
        }
      }
    }
  }

  outputContext.putImageData(outputImage, 0, 0);
  return output;
}

export function composePathTileLayers(options: ComposePathTileOptions): HTMLCanvasElement {
  const canvas = createCanvas(options.tileSize, options.tileSize);
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }

  context.imageSmoothingEnabled = false;

  if (options.outerMask) {
    context.drawImage(expandCanvasAlpha(options.outerMask, options.outerExpansionPx ?? 0), 0, 0);
  }

  const innerCanvas = createCanvas(options.tileSize, options.tileSize);
  const innerContext = innerCanvas.getContext('2d');
  if (!innerContext) {
    return canvas;
  }
  innerContext.imageSmoothingEnabled = false;
  innerContext.drawImage(options.innerMask, 0, 0);

  fillMaskedCanvasWithTexture(
    options.atlasId,
    innerCanvas,
    options.innerMask,
    options.textureImage,
    options.textureTileScale,
  );

  options.decorateInner?.(innerCanvas, options.innerMask);
  context.drawImage(innerCanvas, 0, 0);
  return canvas;
}
